import { EQUIPMENT_PLACES } from "./equipment";
import { BODY_REGIONS, INJURY_MOMENTS, INJURY_NATURES, } from "./injuries";
import { ONBOARDING_STEPS } from "./onboarding";
import { PARQ_QUESTION_COUNT } from "./parq";
import { ATHLETE_SEXES, HOW_HEARD_CHOICES, MAX_HOW_HEARD_OTHER_LENGTH, MAX_MOTIVATION_LENGTH, profileInput, } from "./profile";
import { CONSISTENT_FOR, DAYS_FIXED, GOALS, SESSION_MINUTES, STOPPED_AGO, TRAINED_FOR, trainingConfigInput, } from "./training-config";
/**
 * The ONE step → section-payload mapping of Session 1 (plan D52, `D126`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── WHY THIS LIVES IN THE CONTRACT ─────────────────────────────────────────
 * Two writers answer the intake: the app's stepper (`@repo/client/onboarding`,
 * which compiles a COMPLETE answer set once at the end) and the trainer's
 * write tool (T45, which writes one answer given in chat). They must write the
 * same state for the same answer, and the only way two bundles can share a
 * rule is to import it from the one package both may import. So everything
 * that turns an answer into a request body is here, and the UI half —
 * controls, progress, resume, storage — stays in the client.
 *
 * This file was `apps/mobile/src/lib/onboarding/{intake,commit}.ts` at the tag
 * `expo-app-final`; the behaviour is ported, not redesigned (D126: the
 * branching, the stranded-step rule, the single compile and the single write
 * were measured on the built app).
 *
 * ── PURE ───────────────────────────────────────────────────────────────────
 * zod and plain TypeScript only. No fetch, no storage, no clock, no ids.
 * ═══════════════════════════════════════════════════════════════════════════
 */
// ── The steps ───────────────────────────────────────────────────────────────
/**
 * The body map, as its own step. In the chat it was the `current_pain` step's
 * screen; a stepped intake asks the yes/no and the marking as two things,
 * and answering "no" shortens the path by exactly this step.
 */
export const PAIN_AREAS_STEP = "pain_areas";
/**
 * `D129`'s free text, last, skippable. Not in `ONBOARDING_STEPS` — that list
 * is the server's PROGRESS vocabulary and the motivation is a profile column —
 * but the intake asks it and the compile writes it.
 */
export const MOTIVATION_STEP = "motivation";
/**
 * "Como você nos conheceu?" (`D242`) — the LAST step, after the motivation,
 * and skippable like it. Not in `ONBOARDING_STEPS` for the motivation's
 * reason: it is a profile column (`how_heard`, `how_heard_other`), not a
 * progress step, and no plan depends on it.
 *
 * ONE step, not a choice and a follow-up: "other" opens a short text field on
 * the same screen, so the answer is `[]` (skipped), `[choice]`, or
 * `["other", text]` — see {@link isHowHeardAnswer}.
 */
export const HOW_HEARD_STEP = "how_heard";
/** The three follow-up questions of the experience tree (blueprint item 9) —
 *  local step ids, never in `ONBOARDING_STEPS`. */
export const EXPERIENCE_FOLLOW_UP_STEPS = [
    "experience_consistent_for",
    "experience_stopped_ago",
    "experience_trained_for",
];
/**
 * The four-way home/outdoors equipment choice, each option carrying its
 * `catalog_equipment` id (the server script's `equipmentAskFor`). The gym and
 * mix branches use the catalogue checklist instead.
 */
export const HOME_EQUIPMENT_OPTIONS = [
    { equipmentId: 7, value: "dumbbells" },
    { equipmentId: 3, value: "barbell" },
    { equipmentId: 11, value: "kettlebell" },
    { equipmentId: 4, value: "none" },
];
const WEEKDAY_VALUES = ["0", "1", "2", "3", "4", "5", "6"];
const PARQ_VALUES = ["1", "2", "3", "4", "5", "6", "7"];
const SESSION_LIMITS = ["hard", "preference"];
const EXPERIENCE_STATUSES = ["never", "current", "stopped"];
/**
 * Every choice step's option values — the server script's sixteen plus the
 * three follow-ups and the home chips. Where the contract has a vocabulary it
 * is imported, never restated. `onboarding.<step>.<value>` is the label key of
 * each, which `@repo/client`'s `onboarding-labels.test.ts` checks in both
 * locales.
 *
 * `secondary_goal` and `equipment` list their WIDEST vocabulary here; which
 * values a person is actually offered depends on an earlier answer
 * ({@link intakeVocabulary}).
 */
export const ONBOARDING_CHOICE_VALUES = Object.freeze({
    current_pain: ["no", "yes"],
    days_fixed: DAYS_FIXED,
    days_per_week: ["2", "3", "4", "5", "6"],
    equipment: HOME_EQUIPMENT_OPTIONS.map((option) => option.value),
    equipment_place: EQUIPMENT_PLACES,
    experience: EXPERIENCE_STATUSES,
    experience_consistent_for: CONSISTENT_FOR,
    experience_stopped_ago: STOPPED_AGO,
    experience_trained_for: TRAINED_FOR,
    fixed_weekdays: WEEKDAY_VALUES,
    goal: GOALS,
    how_heard: HOW_HEARD_CHOICES,
    parq: PARQ_VALUES,
    secondary_goal: [...GOALS, "none"],
    session_hard_limit: SESSION_LIMITS,
    session_minutes: SESSION_MINUTES.map(String),
    sex: ATHLETE_SEXES,
});
/**
 * Every step the intake can ask, in order — `ONBOARDING_STEPS` with the body
 * map, the three follow-ups, the motivation and "how did you hear about us"
 * INSERTED, never re-typed.
 * A superset, never a path: {@link pathFor} is the path.
 */
export const INTAKE_STEPS = Object.freeze([
    ...ONBOARDING_STEPS.flatMap((step) => {
        if (step === "current_pain") {
            return [step, PAIN_AREAS_STEP];
        }
        if (step === "experience") {
            return [step, ...EXPERIENCE_FOLLOW_UP_STEPS];
        }
        return [step];
    }),
    MOTIVATION_STEP,
    HOW_HEARD_STEP,
]);
/** A step id this build knows. */
export function isIntakeStep(value) {
    return (typeof value === "string" &&
        INTAKE_STEPS.includes(value));
}
const NONE = [];
function chose(answers, step) {
    return answers[step]?.[0];
}
// ── The path ────────────────────────────────────────────────────────────────
/**
 * Is this step on the path the person is walking? An undecided branch is
 * counted at its LONGEST (weekdays asked, body map asked, the two-follow-up
 * `stopped` experience), so the progress denominator only ever shrinks going
 * forward: at most 20 steps, at least 16.
 */
function isOnPath(step, answers) {
    switch (step) {
        case "fixed_weekdays":
            return chose(answers, "days_fixed") !== "variable";
        case PAIN_AREAS_STEP:
            return chose(answers, "current_pain") !== "no";
        case "experience_consistent_for":
            return chose(answers, "experience") === "current";
        case "experience_stopped_ago":
        case "experience_trained_for": {
            const status = chose(answers, "experience");
            return status === undefined || status === "stopped";
        }
        // `D242`: asked of everyone, on every path, re-entry included — stated
        // rather than left to `default`, because it is the one step whose
        // ANSWER may be nothing at all and still be on the path. A branch that
        // took it off once answered would drop it from `reachableAnswers`, and
        // the compile would never send it.
        case HOW_HEARD_STEP:
            return true;
        default:
            return true;
    }
}
/** The ordered steps this set of answers actually leads through. */
export function pathFor(answers) {
    return INTAKE_STEPS.filter((step) => isOnPath(step, answers));
}
export function intakeVocabulary(step, answers) {
    switch (step) {
        case "date_of_birth":
        case "height":
        case "weight":
        case "parq":
        case MOTIVATION_STEP:
            return { kind: "free" };
        case PAIN_AREAS_STEP:
            return { kind: "pain_reports" };
        case "equipment": {
            const place = chose(answers, "equipment_place");
            return place === "gym" || place === "mix"
                ? { kind: "catalogue_ids" }
                : {
                    kind: "values",
                    values: HOME_EQUIPMENT_OPTIONS.map((option) => option.value),
                };
        }
        case "secondary_goal": {
            // Every goal but the primary, plus an explicit "none" — the server
            // script's `secondaryGoalOptions`.
            const primary = chose(answers, "goal");
            return {
                kind: "values",
                values: [...GOALS.filter((goal) => goal !== primary), "none"],
            };
        }
        default:
            return { kind: "values", values: ONBOARDING_CHOICE_VALUES[step] ?? NONE };
    }
}
/**
 * Answered, AND still an answer to the question this build would ask now.
 *
 * Two steps derive their options from an earlier answer, so going back and
 * changing that answer can leave a stored value the control no longer offers:
 * a secondary goal that became the primary, or checklist ids carried onto the
 * home chips. Both used to fail on the Finish button (a 422 after four writes
 * landed; an `IntakeCompileError` on the chips). Such a value is KEPT — going
 * back must not lose work — but reads as unanswered, so it is asked again and
 * never compiled.
 */
export function isStepAnswered(answers, step) {
    const value = answers[step];
    if (value === undefined) {
        return false;
    }
    if (step === HOW_HEARD_STEP) {
        return isHowHeardAnswer(value);
    }
    const vocabulary = intakeVocabulary(step, answers);
    switch (vocabulary.kind) {
        case "free":
            return true;
        case "pain_reports":
            return value.every((entry) => decodePainReport(entry) !== null);
        case "catalogue_ids":
            return value.every((entry) => isCatalogueId(entry));
        default: {
            const offered = new Set(vocabulary.values);
            return value.every((entry) => offered.has(entry));
        }
    }
}
/**
 * A `how_heard` answer this build can store (`D242`): nothing (skipped), one
 * choice, or `other` followed by its text, trimmed, 1 to
 * `MAX_HOW_HEARD_OTHER_LENGTH` characters. `["other"]` alone is an answer —
 * the text is optional — and text after any other choice is not.
 */
export function isHowHeardAnswer(value) {
    const [choice, text, ...rest] = value;
    if (choice === undefined) {
        return true;
    }
    if (!HOW_HEARD_CHOICES.some((candidate) => candidate === choice)) {
        return false;
    }
    if (text === undefined) {
        return true;
    }
    const trimmed = text.trim();
    return (rest.length === 0 &&
        choice === "other" &&
        trimmed.length > 0 &&
        trimmed.length <= MAX_HOW_HEARD_OTHER_LENGTH);
}
/** Every step of the path is answered — the one moment the stepper compiles. */
export function isComplete(answers) {
    return pathFor(answers).every((step) => isStepAnswered(answers, step));
}
/**
 * The answers the compile reads: the path's, and nothing a changed branch
 * stranded off it. A variable week keeps the weekdays ticked before the
 * change (so switching back restores them), and compiling the RAW set would
 * `PUT` a config that is variable AND names fixed days. {@link compileIntake}
 * calls this itself rather than trusting its caller.
 */
export function reachableAnswers(answers) {
    const out = {};
    for (const step of pathFor(answers)) {
        const value = answers[step];
        if (value !== undefined) {
            out[step] = value;
        }
    }
    return out;
}
const PAIN_REPORT_SEPARATOR = ":";
const PAIN_REPORT_FIELDS = 4;
const INTENSITY_MIN = 1;
const INTENSITY_MAX = 10;
/** `region:nature:intensity:when`. No vocabulary contains the separator. */
export function encodePainReport(report) {
    return [
        report.region,
        report.nature,
        String(report.intensity),
        report.when,
    ].join(PAIN_REPORT_SEPARATOR);
}
/** The inverse, or `null` for anything that is not exactly one report — a
 *  pre-`D155` marker, an unknown vocabulary, an intensity off the scale. */
export function decodePainReport(entry) {
    const parts = entry.split(PAIN_REPORT_SEPARATOR);
    if (parts.length !== PAIN_REPORT_FIELDS) {
        return null;
    }
    const [region, nature, intensity, when] = parts;
    const foundRegion = BODY_REGIONS.find((candidate) => candidate === region);
    const foundNature = INJURY_NATURES.find((candidate) => candidate === nature);
    const foundWhen = INJURY_MOMENTS.find((candidate) => candidate === when);
    const scale = Number(intensity);
    if (foundRegion === undefined ||
        foundNature === undefined ||
        foundWhen === undefined ||
        !Number.isInteger(scale) ||
        scale < INTENSITY_MIN ||
        scale > INTENSITY_MAX) {
        return null;
    }
    return {
        intensity: scale,
        nature: foundNature,
        region: foundRegion,
        when: foundWhen,
    };
}
/** The `pain_areas` answer as reports; entries that do not decode are dropped
 *  (they are what makes the step unanswered, so nothing compiles them). */
export function painReportsOf(answers) {
    return (answers[PAIN_AREAS_STEP] ?? NONE).flatMap((entry) => {
        const report = decodePainReport(entry);
        return report === null ? [] : [report];
    });
}
/** A report as the contract's CURRENT row, the historical branch explicitly
 *  `null` — *"a `null` a client had to write is a `null` it meant"*. */
export function toCurrentRow(report) {
    return {
        howLongAgo: null,
        intensity: report.intensity,
        nature: report.nature,
        occurrence: "current",
        region: report.region,
        status: null,
        type: null,
        when: report.when,
    };
}
/** The inverse, for a CURRENT row; `null` for a historical row. */
export function fromCurrentRow(row) {
    if (row.occurrence !== "current" ||
        row.nature === null ||
        row.intensity === null ||
        row.when === null) {
        return null;
    }
    return {
        intensity: row.intensity,
        nature: row.nature,
        region: row.region,
        when: row.when,
    };
}
const CATALOGUE_ID = /^[1-9]\d*$/;
/** A positive integer, written out — a `catalog_equipment` id as the checklist
 *  stores it. Disjoint from every home chip value by construction. */
export function isCatalogueId(entry) {
    return CATALOGUE_ID.test(entry);
}
/** The `equipment` answer as catalogue ids, whichever branch stored it — what
 *  seeds the checklist, so moving home → gym starts from the chips owned. */
export function equipmentIdsOf(answers) {
    return (answers.equipment ?? NONE).flatMap((entry) => {
        if (isCatalogueId(entry)) {
            return [Number(entry)];
        }
        const chip = HOME_EQUIPMENT_OPTIONS.find((option) => option.value === entry);
        return chip === undefined ? [] : [chip.equipmentId];
    });
}
// ── Compiling a complete answer set ─────────────────────────────────────────
/**
 * An answer set that cannot be compiled — a PROGRAMMING error, never a
 * person's mistake. `isComplete` gates the compile, the controls own each
 * answer's vocabulary and `reachableAnswers` removes contradictions; anything
 * that still does not fit is a caller handing over what it promised not to,
 * and a stack trace at the site beats a `PUT` refused about a field nobody saw.
 */
export class IntakeCompileError extends Error {
    step;
    constructor(step, reason) {
        super(`the intake cannot be compiled: ${step} — ${reason}`);
        this.name = "IntakeCompileError";
        this.step = step;
    }
}
/** `weight`, in kilograms — the series' vocabulary (`D68`). */
export const INTAKE_WEIGHT_KIND = "weight";
export const INTAKE_WEIGHT_UNIT = "kg";
/**
 * A complete answer set into the bodies a commit sends. Throws
 * {@link IntakeCompileError} unless every step of the path is answered — a
 * half-answered intake must never write a half profile (`D126`).
 */
export function compileIntake(answers) {
    const reachable = reachableAnswers(answers);
    if (!isComplete(reachable)) {
        throw new IntakeCompileError("answers", "every step of the path must be answered before the commit");
    }
    return {
        currentPain: compileCurrentPain(reachable),
        equipment: compileEquipment(reachable),
        parq: compileParq(reachable),
        profile: compileProfile(reachable),
        trainingConfig: compileTrainingConfig(reachable),
        weight: compileWeight(reachable),
    };
}
function one(answers, step) {
    const value = answers[step]?.[0];
    if (value === undefined) {
        throw new IntakeCompileError(step, "no answer");
    }
    return value;
}
function admits(step, value, vocabulary) {
    const found = vocabulary.find((candidate) => candidate === value);
    if (found === undefined) {
        throw new IntakeCompileError(step, `'${value}' is not one of the options`);
    }
    return found;
}
function oneOf(answers, step, vocabulary) {
    return admits(step, one(answers, step), vocabulary);
}
function asNumber(step, raw) {
    const value = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(value)) {
        throw new IntakeCompileError(step, `'${raw}' is not a number`);
    }
    return value;
}
function oneNumber(answers, step) {
    return asNumber(step, one(answers, step));
}
function heightOf(answers) {
    const heightCm = oneNumber(answers, "height");
    if (!Number.isInteger(heightCm)) {
        throw new IntakeCompileError("height", "a height is a whole centimetre");
    }
    return heightCm;
}
/**
 * `date_of_birth`, `sex`, `height`, and `D129`'s sentence — one row.
 *
 * The motivation is sent EXPLICITLY, `null` included: absent means *leave the
 * stored sentence alone*, and in an intake "I skipped it" is an answer.
 */
function compileProfile(answers) {
    return {
        dateOfBirth: one(answers, "date_of_birth"),
        heightCm: heightOf(answers),
        ...compileHowHeard(answers),
        motivation: compileMotivation(answers),
        sex: oneOf(answers, "sex", ATHLETE_SEXES),
    };
}
/**
 * `D242`'s answer as the two profile fields, both EXPLICIT — `null` included,
 * on the motivation's rule: in an intake "I skipped it" is an answer, and
 * absent would mean *leave what is stored*.
 */
function compileHowHeard(answers) {
    const value = answers[HOW_HEARD_STEP] ?? NONE;
    if (!isHowHeardAnswer(value)) {
        throw new IntakeCompileError(HOW_HEARD_STEP, "not a choice this build offers");
    }
    const [choice, text] = value;
    const howHeard = HOW_HEARD_CHOICES.find((candidate) => candidate === choice) ?? null;
    return {
        howHeard,
        howHeardOther: howHeard === "other" ? (text?.trim() ?? null) : null,
    };
}
function compileMotivation(answers) {
    const sentence = (answers.motivation?.[0] ?? "").trim();
    if (sentence.length === 0) {
        return null;
    }
    if (sentence.length > MAX_MOTIVATION_LENGTH) {
        throw new IntakeCompileError(MOTIVATION_STEP, `longer than ${MAX_MOTIVATION_LENGTH} characters`);
    }
    return sentence;
}
/** The weigh-in, as one point of the series. No `measuredAt`: omitted means
 *  now on the SERVER's clock, never the phone's. */
function compileWeight(answers) {
    return {
        kind: INTAKE_WEIGHT_KIND,
        unit: INTAKE_WEIGHT_UNIT,
        value: oneNumber(answers, "weight"),
    };
}
/** Seven positional `"yes"`/`"no"` into the seven-tuple — the position IS the
 *  question, so a set of six or eight is refused rather than shifted. */
function parqOf(values) {
    if (values.length !== PARQ_QUESTION_COUNT) {
        throw new IntakeCompileError("parq", `${values.length} answers, expected ${PARQ_QUESTION_COUNT}`);
    }
    return {
        answers: values.map((value, index) => {
            if (value !== "yes" && value !== "no") {
                throw new IntakeCompileError("parq", `question ${index + 1} is '${value}', not yes or no`);
            }
            return value === "yes";
        }),
    };
}
function compileParq(answers) {
    return parqOf(answers.parq ?? NONE);
}
/**
 * The equipment, as the `equipment_place` branch asked it: catalogue ids on
 * the gym/mix checklist, the four chips at home and outdoors. Deciding by the
 * branch, not by sniffing the values, means this and the control can never
 * disagree about which question was asked.
 *
 * Since T29 the place is stored (`equipment.place`), so the intake's answer is
 * written with the list rather than being only a branch.
 */
function compileEquipment(answers) {
    const place = oneOf(answers, "equipment_place", EQUIPMENT_PLACES);
    const values = answers.equipment ?? NONE;
    const ids = place === "gym" || place === "mix"
        ? values.map(catalogueId)
        : values.map(chipId);
    // The contract refuses a repeated id, and two chips can name one row.
    return { available: [...new Set(ids)], place };
}
function catalogueId(value) {
    if (!isCatalogueId(value)) {
        throw new IntakeCompileError("equipment", `'${value}' is not a catalogue id`);
    }
    return Number(value);
}
function chipId(value) {
    const option = HOME_EQUIPMENT_OPTIONS.find((candidate) => candidate.value === value);
    if (option === undefined) {
        throw new IntakeCompileError("equipment", `'${value}' is not a chip`);
    }
    return option.equipmentId;
}
function reportsFrom(entries) {
    return entries.map((entry) => {
        const report = decodePainReport(entry);
        if (report === null) {
            throw new IntakeCompileError(PAIN_AREAS_STEP, "an entry is not a report");
        }
        return toCurrentRow(report);
    });
}
/** The body map's reports as CURRENT rows; nothing hurts → no rows. */
function compileCurrentPain(answers) {
    if (answers.current_pain?.[0] === "no") {
        return [];
    }
    return reportsFrom(answers[PAIN_AREAS_STEP] ?? NONE);
}
function weekdayOf(value) {
    const day = Number(value);
    if (!WEEKDAY_VALUES.some((candidate) => candidate === value)) {
        throw new IntakeCompileError("fixed_weekdays", `'${value}' is not a weekday 0-6`);
    }
    return day;
}
function sessionMinutesOf(raw) {
    const value = asNumber("session_minutes", raw);
    const band = SESSION_MINUTES.find((candidate) => candidate === value);
    if (band === undefined) {
        throw new IntakeCompileError("session_minutes", `${value} is not one of the bands`);
    }
    return band;
}
function daysPerWeekOf(raw) {
    return Number(admits("days_per_week", raw, ONBOARDING_CHOICE_VALUES.days_per_week ?? NONE));
}
function secondaryGoalOf(raw) {
    // The onboarding offers *"none"* explicitly; `null` is what it means.
    return raw === "none" ? null : admits("secondary_goal", raw, GOALS);
}
/**
 * The eight config-backed steps as ONE row. `fixedWeekdays` is empty on a
 * variable week without a special case: the step is off the path there, so
 * `reachableAnswers` has already removed any weekdays ticked before the change.
 */
function compileTrainingConfig(answers) {
    return {
        daysFixed: oneOf(answers, "days_fixed", DAYS_FIXED),
        daysPerWeek: daysPerWeekOf(one(answers, "days_per_week")),
        experience: compileExperience(answers),
        fixedWeekdays: (answers.fixed_weekdays ?? NONE).map(weekdayOf),
        primaryGoal: oneOf(answers, "goal", GOALS),
        secondaryGoal: secondaryGoalOf(one(answers, "secondary_goal")),
        sessionMinutes: sessionMinutesOf(one(answers, "session_minutes")),
        // `hard` = *"I cannot go over"*, `preference` = *"I would rather not"*.
        sessionMinutesIsHardLimit: oneOf(answers, "session_hard_limit", SESSION_LIMITS) === "hard",
    };
}
/** The experience tree's three shapes — a discriminated union, because
 *  `{ status: "never", stoppedAgo: "gt3y" }` is a row the engine believes. */
function compileExperience(answers) {
    const status = oneOf(answers, "experience", EXPERIENCE_STATUSES);
    if (status === "never") {
        return { status };
    }
    if (status === "current") {
        return {
            consistentFor: oneOf(answers, "experience_consistent_for", CONSISTENT_FOR),
            status,
        };
    }
    return {
        status,
        stoppedAgo: oneOf(answers, "experience_stopped_ago", STOPPED_AGO),
        trainedFor: oneOf(answers, "experience_trained_for", TRAINED_FOR),
    };
}
export function mergeInjuries(currentPain, stored) {
    const carried = new Map();
    for (const row of stored) {
        if (row.occurrence !== "current") {
            continue;
        }
        const kept = {};
        if (typeof row.note === "string" && row.note !== "") {
            kept.note = row.note;
        }
        if (typeof row.since === "string") {
            kept.since = row.since;
        }
        if (row.trend !== null && row.trend !== undefined) {
            kept.trend = row.trend;
        }
        if (Object.keys(kept).length > 0) {
            carried.set(row.region, kept);
        }
    }
    const current = currentPain.map((row) => {
        const kept = carried.get(row.region);
        if (kept === undefined) {
            return row;
        }
        const add = {};
        if (kept.note !== undefined && row.note === undefined) {
            add.note = kept.note;
        }
        if (kept.since !== undefined && row.since === undefined) {
            add.since = kept.since;
        }
        if (kept.trend !== undefined && row.trend === undefined) {
            add.trend = kept.trend;
        }
        return Object.keys(add).length === 0 ? row : { ...row, ...add };
    });
    const historical = stored.filter((row) => row.occurrence === "historical");
    return { injuries: [...current, ...historical] };
}
/** The order the writes land in: profile first (every reader keys off it),
 *  `trainingConfig` LAST — its row is what the server reads as *"Session 1 is
 *  finished"* and what the plan proposer waits for, so a commit that dies
 *  half-way must leave it absent. */
export const INTAKE_WRITE_SECTIONS = [
    "profile",
    "measurement",
    "parq",
    "injuries",
    "equipment",
    "trainingConfig",
];
/**
 * The writes an answer set produces, in {@link INTAKE_WRITE_SECTIONS} order.
 *
 *   - **One answered step** (`{ [step]: value }`, the trainer's tool) →
 *     {@link intakeWritesForStep}: only the sections that step touches, and
 *     only when the step fully determines them.
 *   - **Anything else** → the whole intake, compiled once: throws
 *     {@link IntakeCompileError} unless {@link isComplete}. A half-answered
 *     intake never writes a half profile (`D126`).
 */
export function intakeWrites(answers, current) {
    const answered = INTAKE_STEPS.filter((step) => answers[step] !== undefined);
    const [only] = answered;
    if (answered.length === 1 && only !== undefined) {
        return intakeWritesForStep(only, answers[only] ?? NONE, current);
    }
    const plan = compileIntake(answers);
    return [
        { body: plan.profile, section: "profile" },
        { body: plan.weight, section: "measurement" },
        { body: plan.parq, section: "parq" },
        {
            body: mergeInjuries(plan.currentPain, current.injuries),
            section: "injuries",
        },
        { body: plan.equipment, section: "equipment" },
        { body: plan.trainingConfig, section: "trainingConfig" },
    ];
}
/**
 * The writes ONE answer produces on its own — the trainer's entry point.
 *
 * A step writes only what it fully determines. A section PUT replaces a whole
 * row, so the answer is merged onto the stored section in `current`; when there
 * is none, or when the merged row would still need a later answer (a `fixed`
 * week with no weekdays, `stopped` without both follow-ups, pain `yes` before
 * the body map), the result is `[]` and nothing is written. A merged row the
 * contract refuses — a primary goal equal to the stored secondary — is `[]`
 * too: the other answer has to change first.
 *
 * A value outside the step's own vocabulary throws {@link IntakeCompileError}:
 * that is the caller's mistake, not a missing answer.
 *
 * Idempotent over replace-semantics sections. The weigh-in APPENDS
 * (`POST /me/measurements`); a caller that may repeat it supplies its own
 * idempotency key on the body (`D154`).
 */
export function intakeWritesForStep(step, value, current) {
    const answers = { [step]: value };
    switch (step) {
        case "date_of_birth":
        case "sex":
        case "height":
        case MOTIVATION_STEP:
        case HOW_HEARD_STEP:
            return profileWrite(step, answers, current);
        case "weight":
            return [{ body: compileWeight(answers), section: "measurement" }];
        case "parq":
            return [{ body: parqOf(value), section: "parq" }];
        case "current_pain":
            return oneOf(answers, "current_pain", ["no", "yes"]) === "no"
                ? [injuriesWrite([], current)]
                : [];
        case PAIN_AREAS_STEP:
            return [injuriesWrite(reportsFrom(value), current)];
        case "equipment_place":
            return placeWrite(oneOf(answers, step, EQUIPMENT_PLACES), current);
        case "equipment":
            return [
                {
                    // Either branch's shape, decoded entry by entry: the stored place
                    // is not re-sent (T29 — absent keeps it).
                    body: { available: [...new Set(equipmentIdsStrict(value))] },
                    section: "equipment",
                },
            ];
        default:
            return configWrite(step, answers, current.trainingConfig ?? null);
    }
}
function injuriesWrite(currentPain, current) {
    return {
        body: mergeInjuries(currentPain, current.injuries),
        section: "injuries",
    };
}
function equipmentIdsStrict(values) {
    return values.map((entry) => isCatalogueId(entry) ? Number(entry) : chipId(entry));
}
function placeWrite(place, current) {
    if (current.equipment === undefined) {
        return [];
    }
    return [
        {
            body: { available: [...(current.equipment?.available ?? [])], place },
            section: "equipment",
        },
    ];
}
function profileWrite(step, answers, current) {
    // Parsed first, so a bad value throws whether or not a row is stored.
    const patch = profilePatch(step, answers);
    const stored = current.profile ?? null;
    if (stored === null) {
        return [];
    }
    const body = {
        dateOfBirth: stored.dateOfBirth,
        heightCm: stored.heightCm,
        sex: stored.sex,
        ...patch,
    };
    return profileInput.safeParse(body).success
        ? [{ body, section: "profile" }]
        : [];
}
function profilePatch(step, answers) {
    switch (step) {
        case "date_of_birth":
            return { dateOfBirth: one(answers, step) };
        case "sex":
            return { sex: oneOf(answers, step, ATHLETE_SEXES) };
        case "height":
            return { heightCm: heightOf(answers) };
        case HOW_HEARD_STEP:
            return compileHowHeard(answers);
        default:
            // A skipped sentence is `null`, written explicitly (`D129`).
            return { motivation: compileMotivation(answers) };
    }
}
function configPatch(step, answers, stored) {
    switch (step) {
        case "goal":
            return { primaryGoal: oneOf(answers, step, GOALS) };
        case "secondary_goal":
            return { secondaryGoal: secondaryGoalOf(one(answers, step)) };
        case "days_per_week":
            return { daysPerWeek: daysPerWeekOf(one(answers, step)) };
        case "days_fixed": {
            const daysFixed = oneOf(answers, step, DAYS_FIXED);
            if (daysFixed === "variable") {
                return { daysFixed, fixedWeekdays: [] };
            }
            // A fixed or mixed week names its days; with none stored yet, the
            // weekdays answer is the later one this write waits for.
            return stored === null || stored.fixedWeekdays.length === 0
                ? null
                : { daysFixed };
        }
        case "fixed_weekdays":
            if (stored === null || stored.daysFixed === "variable") {
                return null;
            }
            return { fixedWeekdays: (answers[step] ?? NONE).map(weekdayOf) };
        case "session_minutes":
            return { sessionMinutes: sessionMinutesOf(one(answers, step)) };
        case "session_hard_limit":
            return {
                sessionMinutesIsHardLimit: oneOf(answers, step, SESSION_LIMITS) === "hard",
            };
        case "experience": {
            const status = oneOf(answers, step, EXPERIENCE_STATUSES);
            if (status === "never") {
                return { experience: { status } };
            }
            // `current` and `stopped` need their follow-ups: keep a stored tree
            // that already has this status, and otherwise wait for them.
            return stored?.experience.status === status
                ? { experience: stored.experience }
                : null;
        }
        case "experience_consistent_for":
            // Only ever asked of `current`, so the answer determines the tree.
            return {
                experience: {
                    consistentFor: oneOf(answers, step, CONSISTENT_FOR),
                    status: "current",
                },
            };
        case "experience_stopped_ago": {
            // `stopped` needs BOTH follow-ups; one of them completes the tree only
            // when the other is already stored.
            const stoppedAgo = oneOf(answers, step, STOPPED_AGO);
            return stored?.experience.status === "stopped"
                ? { experience: { ...stored.experience, stoppedAgo } }
                : null;
        }
        case "experience_trained_for": {
            const trainedFor = oneOf(answers, step, TRAINED_FOR);
            return stored?.experience.status === "stopped"
                ? { experience: { ...stored.experience, trainedFor } }
                : null;
        }
        default:
            throw new IntakeCompileError(step, "not a training-config step");
    }
}
function configWrite(step, answers, stored) {
    const patch = configPatch(step, answers, stored);
    if (patch === null || stored === null) {
        return [];
    }
    const body = {
        daysFixed: stored.daysFixed,
        daysPerWeek: stored.daysPerWeek,
        experience: stored.experience,
        fixedWeekdays: [...stored.fixedWeekdays],
        primaryGoal: stored.primaryGoal,
        secondaryGoal: stored.secondaryGoal,
        sessionMinutes: stored.sessionMinutes,
        sessionMinutesIsHardLimit: stored.sessionMinutesIsHardLimit,
        ...patch,
    };
    return trainingConfigInput.safeParse(body).success
        ? [{ body, section: "trainingConfig" }]
        : [];
}
// ── The body map's geometry ─────────────────────────────────────────────────
/**
 * The shared canvas both figures are drawn on (`T41`). The map IS the
 * contract's region list — 17 regions, unique, in `BODY_REGIONS` order — so the
 * geometry sits beside the vocabulary it must match, where a test holds it.
 *
 * Each figure is a HUMAN SILHOUETTE — head, neck, torso, arms held slightly
 * away from the body, legs — and every region is a closed path that covers one
 * part of it, so the two tile the body between them (`T74`). The regions were
 * plain rectangles until then, which read as a stack of boxes.
 *
 * Paired joints are drawn as the LIMB SEGMENT that ends in them (`elbow_r` is
 * the right upper arm, `knee_r` the thigh) because a fingertip cannot hit a
 * joint line. The front figure faces the reader, so `_r` regions sit on the
 * viewer's LEFT, as on every clinical body chart; `thoracic` and `lumbar` are
 * on the back figure.
 *
 * Two properties the paths must keep, because a test reads the NUMBERS rather
 * than rendering anything:
 * - only absolute `M`, `L`, `C` and `Z`, so every number pairs off as `x y`;
 * - no coordinate outside its figure's outline, so no region floats off the
 *   body. Both halves of a pair are the same shape mirrored about `x = 100`.
 */
export const BODY_FIGURE_VIEW_BOX = "0 0 200 300";
function mapRegion(id, figure, path) {
    return { figure, id, labelKey: `bodymap.region.${id}`, path };
}
/** All 17, in `BODY_REGIONS` order — the map's reading order, top to bottom. */
export const BODY_MAP_REGIONS = Object.freeze([
    mapRegion("cervical", "front", "M90 51 L110 51 C111 58 112 64 112 70 L88 70 C88 64 89 58 90 51 Z"),
    mapRegion("shoulder_r", "front", "M70 77 C58 79 47 81 39 88 C35 96 34 105 34 118 L57 118 C57 104 62 94 69 87 C70 84 70 80 70 77 Z"),
    mapRegion("shoulder_l", "front", "M130 77 C142 79 153 81 161 88 C165 96 166 105 166 118 L143 118 C143 104 138 94 131 87 C130 84 130 80 130 77 Z"),
    mapRegion("elbow_r", "front", "M34 118 L57 118 C56 134 54 152 53 168 L31 168 C32 152 33 134 34 118 Z"),
    mapRegion("elbow_l", "front", "M166 118 L143 118 C144 134 146 152 147 168 L169 168 C168 152 167 134 166 118 Z"),
    mapRegion("wrist_r", "front", "M31 168 L53 168 C52 180 51 192 50 205 C50 218 46 226 40 226 C34 226 30 218 30 205 C30 194 30 182 31 168 Z"),
    mapRegion("wrist_l", "front", "M169 168 L147 168 C148 180 149 192 150 205 C150 218 154 226 160 226 C166 226 170 218 170 205 C170 194 170 182 169 168 Z"),
    mapRegion("thoracic", "back", "M70 77 C76 74 82 72 88 70 L112 70 C118 72 124 74 130 77 C132 91 132 110 129 126 L71 126 C68 110 68 91 70 77 Z"),
    mapRegion("lumbar", "back", "M71 126 L129 126 C127 143 126 155 125 166 L75 166 C74 155 73 143 71 126 Z"),
    mapRegion("hip_r", "front", "M75 158 C70 163 68 169 68 176 C68 184 69 189 70 192 L100 192 L100 158 Z"),
    mapRegion("hip_l", "front", "M125 158 C130 163 132 169 132 176 C132 184 131 189 130 192 L100 192 L100 158 Z"),
    mapRegion("knee_r", "front", "M70 192 C68 210 69 228 72 244 L97 244 C99 226 100 210 100 192 Z"),
    mapRegion("knee_l", "front", "M130 192 C132 210 131 228 128 244 L103 244 C101 226 100 210 100 192 Z"),
    mapRegion("ankle_r", "front", "M72 244 C73 258 75 272 77 284 C76 291 72 294 68 295 L92 295 C93 291 93 288 92 284 C94 266 96 254 97 244 Z"),
    mapRegion("ankle_l", "front", "M128 244 C127 258 125 272 123 284 C124 291 128 294 132 295 L108 295 C107 291 107 288 108 284 C106 266 104 254 103 244 Z"),
    mapRegion("abdomen", "front", "M71 120 L129 120 C127 138 126 150 125 158 L75 158 C74 150 73 138 71 120 Z"),
    mapRegion("chest", "front", "M70 77 C76 74 82 72 88 70 L112 70 C118 72 124 74 130 77 C132 89 132 105 129 120 L71 120 C68 105 68 89 70 77 Z"),
]);
/**
 * The non-tappable body each figure's regions are laid over: drawn first, and
 * the only thing that gives the head, the hands and the feet — the parts no
 * region covers. The two differ where a back view differs: the front's toes
 * point out, the back's heels face the reader.
 *
 * Each is one closed path, the left half traced from the crown to the crotch
 * and mirrored back up, so a figure cannot go lopsided.
 */
export const FIGURE_OUTLINES = Object.freeze({
    back: "M100 3 C86 5 77 15 77 28 C77 40 83 49 90 51 C89 58 88 64 88 70 C82 72 76 74 70 77 C58 79 47 81 39 88 C35 96 34 103 34 112 C33 130 32 150 31 168 C30 182 30 194 30 205 C30 218 34 226 40 226 C46 226 50 218 50 205 C51 192 52 180 53 168 C54 150 56 130 57 112 C59 101 63 93 69 87 C68 99 68 110 71 120 C73 138 74 150 75 158 C70 163 68 169 68 176 C68 184 69 189 70 192 C68 210 69 228 72 244 C73 258 75 272 77 284 C76 291 72 294 68 295 L94 295 C93 291 93 288 92 284 C94 266 96 254 97 244 C99 226 100 210 100 194 C100 210 101 226 103 244 C104 254 106 266 108 284 C107 288 107 291 106 295 L132 295 C128 294 124 291 123 284 C125 272 127 258 128 244 C131 228 132 210 130 192 C131 189 132 184 132 176 C132 169 130 163 125 158 C126 150 127 138 129 120 C132 110 132 99 131 87 C137 93 141 101 143 112 C144 130 146 150 147 168 C148 180 149 192 150 205 C150 218 154 226 160 226 C166 226 170 218 170 205 C170 194 170 182 169 168 C168 150 167 130 166 112 C166 103 165 96 161 88 C153 81 142 79 130 77 C124 74 118 72 112 70 C112 64 111 58 110 51 C117 49 123 40 123 28 C123 15 114 5 100 3 Z",
    front: "M100 3 C86 5 77 15 77 28 C77 40 83 49 90 51 C89 58 88 64 88 70 C82 72 76 74 70 77 C58 79 47 81 39 88 C35 96 34 103 34 112 C33 130 32 150 31 168 C30 182 30 194 30 205 C30 218 34 226 40 226 C46 226 50 218 50 205 C51 192 52 180 53 168 C54 150 56 130 57 112 C59 101 63 93 69 87 C68 99 68 110 71 120 C73 138 74 150 75 158 C70 163 68 169 68 176 C68 184 69 189 70 192 C68 210 69 228 72 244 C73 258 75 272 77 284 C76 291 72 294 68 295 L92 295 C93 291 93 288 92 284 C94 266 96 254 97 244 C99 226 100 210 100 194 C100 210 101 226 103 244 C104 254 106 266 108 284 C107 288 107 291 108 295 L132 295 C128 294 124 291 123 284 C125 272 127 258 128 244 C131 228 132 210 130 192 C131 189 132 184 132 176 C132 169 130 163 125 158 C126 150 127 138 129 120 C132 110 132 99 131 87 C137 93 141 101 143 112 C144 130 146 150 147 168 C148 180 149 192 150 205 C150 218 154 226 160 226 C166 226 170 218 170 205 C170 194 170 182 169 168 C168 150 167 130 166 112 C166 103 165 96 161 88 C153 81 142 79 130 77 C124 74 118 72 112 70 C112 64 111 58 110 51 C117 49 123 40 123 28 C123 15 114 5 100 3 Z",
});
// ── The equipment half of the checklist ─────────────────────────────────────
/**
 * The catalogue ids a home means — body weight and the three home free
 * weights, the same four the chips carry — so the checklist's *Home* quick
 * action and the chip branch agree on what a home is.
 */
export function homeEquipmentIds() {
    return HOME_EQUIPMENT_OPTIONS.map((option) => option.equipmentId);
}
//# sourceMappingURL=intake.js.map